// Common
import {
  AfterViewInit,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  Injector,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  Type,
  ViewChild,
  ViewContainerRef,
  signal,
} from '@angular/core';

// RX
import { Subject, fromEvent, type Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Service
import { DragDrop, DragRef, Point } from '@angular/cdk/drag-drop';

// Components
import { BaseModalComponent } from './../base-modal/base-modal.component';

// Types
import type { Icon } from '@frontendlib/common/providers/icons/icons';
import { LSNumber, LocalStorageItem } from '@frontendlib/common/decorators/local-storage.decorator';
import { ModalFrame, type ModalMinimizedStatus, type ModalTitleUpdateEvent } from '@modules/modal/types/modal-frame';
import { ModalData } from '@modules/modal/types/modal-config';

// Injection Tokens
import CloseToken from '@modules/modal/types/modal-close.injection-token';
import { DeferInstanceDecorator, applyInstanceDecorators } from '@sharedlib/common/lang/DeferInstanceDecorator';

const HEADER_HEIGHT = 40;

type ModalChild = BaseModalComponent & { minimizedStatus?: Observable<ModalMinimizedStatus | null> };

@Component({
  selector: 'app-modal-wrapper',
  templateUrl: './modal-wrapper.component.html',
  styleUrls: ['./modal-wrapper.component.less'],
  standalone: false,
})
export class ModalWrapperComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() title: string;
  @Input() appearance: 'amethyst' | 'default' = 'amethyst';
  @Input() frame: ModalFrame;
  @Input() icon: Icon;
  @Input() childComponent: Type<any>;
  @Input() closeTestId?: string;

  @Output() onClose = new EventEmitter();
  @Output() collapsed = new EventEmitter<boolean>();
  @Output() focused = new EventEmitter<void>();
  @Output() titleUpdated = new EventEmitter<ModalTitleUpdateEvent>();
  @Output() hiddenChange = new EventEmitter<boolean>();

  public childComponentData: ModalData;
  public overlaps: boolean;
  public resize: boolean;
  public minimized: boolean;
  public hidden = false;
  public collapsible: boolean;
  public expandable: boolean;

  public get minimizedStatus(): ModalMinimizedStatus | null {
    return this.minimizedStatusState();
  }

  @ViewChild('modalAnchor', { read: ViewContainerRef, static: true })
  private readonly modalAnchor: ViewContainerRef;

  private componentRef: ComponentRef<ModalChild>;
  private readonly minimizedStatusState = signal<ModalMinimizedStatus | null>(null);
  private sizeChanges = new Subject<void>();
  private alive = new Subject<void>();
  private dragElement: DragRef;
  private animationTimer: ReturnType<typeof setTimeout> | null = null;
  private minSize = { width: 400, height: 300 };
  private closeSubject = new Subject<void>();
  private variant: string;

  @DeferInstanceDecorator(LSNumber({ lsKey: 'modal', min: 0 }))
  private readonly bounding: LocalStorageItem<number>;

  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly ngZone = inject(NgZone);
  private readonly dragDrop = inject(DragDrop);
  private readonly injector = inject(Injector);

  constructor() {
    applyInstanceDecorators(this, ModalWrapperComponent);
  }

  /**
   * Component lifecycle
   */

  ngOnInit() {
    this.variant = this.childComponent.name
      .replace('Component', '')
      .replace(/([a-z])([A-Z])/g, '$1_$2')
      .toLowerCase();

    const injector = Injector.create({
      providers: [{ provide: CloseToken, useFactory: () => this.closeSubject }],
      parent: this.injector,
    });

    this.componentRef = this.modalAnchor.createComponent(this.childComponent, { injector });

    // Inject data and handlers
    if (this.childComponentData) {
      Object.keys(this.childComponentData).forEach((key) => {
        this.componentRef.instance[key] = this.childComponentData[key];
      });
    }

    this.componentRef.instance.collapsed?.pipe(takeUntil(this.alive))?.subscribe((minimized) => {
      if (this.hidden) {
        return;
      }
      if (this.dragElement) {
        this.dragElement.disabled = minimized;
      }
      this.collapsed.emit(minimized);
    });

    this.componentRef.instance.closed?.pipe(takeUntil(this.alive))?.subscribe(() => this.onClose.emit());

    this.componentRef.instance.hiddenChange
      ?.pipe(takeUntil(this.alive))
      ?.subscribe((hidden) => this.setHidden(hidden));

    if (this.componentRef.instance.titleUpdated) {
      this.componentRef.instance.titleUpdated.pipe(takeUntil(this.alive)).subscribe((data) => {
        if (data.title) {
          this.title = data.title;
        }
        this.titleUpdated.emit(data);
      });
    }

    this.componentRef.instance.minimizedStatus
      ?.pipe(takeUntil(this.alive))
      ?.subscribe((status) => this.minimizedStatusState.set(status));

    this.setSizeAndPosition(this.frame);

    if (this.overlaps) {
      this.overlapsPosition();
    }

    this.closeSubject.pipe(takeUntil(this.alive)).subscribe(() => {
      this.close();
    });
  }

  ngAfterViewInit() {
    const dragHandler = this.elementRef.nativeElement.querySelector('.modal-heading');

    if (dragHandler) {
      this.dragElement = this.dragDrop.createDrag(this.elementRef.nativeElement);
      // this.dragElement.withBoundaryElement(document.body);
      this.dragElement.withHandles([dragHandler]);
      this.dragElement.constrainPosition = (
        userPointerPosition: Point,
        _dragRef: DragRef,
        dimensions: DOMRect,
        pickupPositionInElement: Point,
      ) => ({
        x: Math.min(
          Math.max(userPointerPosition.x - pickupPositionInElement.x, 40 - dimensions.width),
          window.innerWidth - 40,
        ),
        y: Math.min(Math.max(userPointerPosition.y - pickupPositionInElement.y, 0), window.innerHeight - HEADER_HEIGHT),
      });

      this.dragElement.ended.pipe(takeUntil(this.alive)).subscribe(() => {
        this.saveSizeAndPosition();
      });
    }
  }

  ngOnDestroy() {
    if (this.animationTimer !== null) {
      clearTimeout(this.animationTimer);
    }
    this.sizeChanges.next();
    this.sizeChanges.complete();
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Public Methods
   */

  public toggleCollapse(): void {
    if (!this.minimized && this.componentRef?.instance.canLeaveHost?.() === false) return;
    this.minimized = !this.minimized;
    if (this.dragElement) {
      this.dragElement.disabled = this.minimized;
    }
    this.collapsed.emit(this.minimized);
  }

  public collapse(minimized: boolean, index: number = 0): void {
    this.minimized = minimized;

    if (this.animationTimer !== null) {
      clearTimeout(this.animationTimer);
      this.animationTimer = null;
    }

    const element = this.elementRef.nativeElement;
    this.renderer.addClass(element, 'animating');
    // Force reflow so the browser registers current state with transition active
    void element.offsetHeight;

    if (minimized) {
      const minimizedFrame: ModalFrame = {
        x: 'calc(100% - 370px)',
        y: `calc(100% - ${(index + 1) * 32 + index * 1}px)`,
        width: '320px',
        height: '32px',
      };
      this.setSizeAndPosition(minimizedFrame);
    } else {
      this.dragElement?.reset();
      this.setSizeAndPosition(this.frame);
    }

    this.animationTimer = setTimeout(() => {
      this.renderer.removeClass(element, 'animating');
      this.animationTimer = null;
    }, 250);
  }

  public setZIndex(zIndex: number): void {
    this.renderer.setStyle(this.elementRef.nativeElement, 'z-index', zIndex);
  }

  public bringToFront(): void {
    this.focused.emit();
  }

  /** Takes the whole window off the screen, out of pointer and focus reach, without destroying the child. */
  private setHidden(hidden: boolean): void {
    if (this.hidden === hidden) {
      return;
    }
    this.hidden = hidden;
    const element = this.elementRef.nativeElement;
    if (hidden) {
      this.renderer.setStyle(element, 'display', 'none');
    } else {
      this.renderer.removeStyle(element, 'display');
    }
    this.hiddenChange.emit(hidden);
  }

  /**
   * Resize
   */

  handleResize(event: MouseEvent) {
    this.ngZone.runOutsideAngular(() => {
      this.sizeChanges.next();

      let lastCoordinates = {
        x: event.x,
        y: event.y,
      };

      fromEvent(window, 'mousemove')
        .pipe(takeUntil(this.sizeChanges))
        .subscribe((moveEvent: MouseEvent) => {
          const delta = {
            x: moveEvent.x - lastCoordinates.x,
            y: moveEvent.y - lastCoordinates.y,
          };

          const currentSize = {
            width: this.elementRef.nativeElement.clientWidth,
            height: this.elementRef.nativeElement.clientHeight,
          };

          const newSize = {
            width: currentSize.width + delta.x > this.minSize.width ? currentSize.width + delta.x : this.minSize.width,
            height:
              currentSize.height + delta.y > this.minSize.height ? currentSize.height + delta.y : this.minSize.height,
          };

          if (currentSize.width !== newSize.width) {
            this.renderer.setStyle(this.elementRef.nativeElement, 'width', `${newSize.width}px`);
          }

          if (currentSize.height !== newSize.height) {
            this.renderer.setStyle(this.elementRef.nativeElement, 'height', `${newSize.height}px`);
          }

          // Ovverride previous coordinates to actual amount of pixels windiw size changed
          lastCoordinates.x = lastCoordinates.x + (this.elementRef.nativeElement.clientWidth - currentSize.width);
          lastCoordinates.y = lastCoordinates.y + (this.elementRef.nativeElement.clientHeight - currentSize.height);
        });

      fromEvent(window, 'mouseup')
        .pipe(takeUntil(this.sizeChanges))
        .subscribe((_mouseEvent: MouseEvent) => {
          this.saveSizeAndPosition();
          lastCoordinates = null;
          this.sizeChanges.next();
        });
    });
  }

  overlapsPosition() {
    const modalShiftSize = 30;
    const minMargin = 20;
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    const newX = rect.x + modalShiftSize;
    const newY = rect.y + modalShiftSize;

    // Ensure modal stays fully on screen with margin
    const maxX = window.innerWidth - rect.width - minMargin;
    const maxY = window.innerHeight - rect.height - minMargin;

    // If new position would push modal off-screen, reset to center
    if (newX > maxX || newY > maxY || newX < minMargin || newY < minMargin) {
      // Clear stored position to reset to center (handled by setSizeAndPosition)
      this.bounding.set(null, `${this.variant}.x`);
      this.bounding.set(null, `${this.variant}.y`);
    } else {
      this.bounding.set(newX, `${this.variant}.x`);
      this.bounding.set(newY, `${this.variant}.y`);
    }
    this.setSizeAndPosition(this.frame);
  }

  saveSizeAndPosition() {
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    this.bounding.set(rect.x, `${this.variant}.x`);
    this.bounding.set(rect.y, `${this.variant}.y`);
    if (this.resize) {
      this.bounding.set(rect.width, `${this.variant}.width`);
      this.bounding.set(rect.height, `${this.variant}.height`);
    }
  }

  setSizeAndPosition(frame?: ModalFrame) {
    if (!frame) {
      return;
    }
    const element = this.elementRef.nativeElement;

    // Only apply localStorage values when NOT minimized
    if (!this.minimized) {
      // First, get stored dimensions if resizable
      if (this.resize) {
        const width = this.bounding.getSync(`${this.variant}.width`);
        const height = this.bounding.getSync(`${this.variant}.height`);
        if (width) {
          frame.width = `${width}px`;
        }
        if (height) {
          frame.height = `${height}px`;
        }
      }

      // Parse modal dimensions for boundary checking
      const modalWidth = parseInt(frame.width, 10) || 800;
      const modalHeight = parseInt(frame.height, 10) || 500;

      // Minimum margin from screen edges
      const minMargin = 20;

      // Calculate valid position range to keep modal fully on screen with margin
      const minX = minMargin;
      const minY = minMargin;
      const maxX = Math.max(minMargin, window.innerWidth - modalWidth - minMargin);
      const maxY = Math.max(minMargin, window.innerHeight - modalHeight - minMargin);

      // Calculate centered position for reset
      const centeredX = Math.max(minMargin, Math.floor((window.innerWidth - modalWidth) / 2));
      const centeredY = Math.max(minMargin, Math.floor((window.innerHeight - modalHeight) / 2));

      const x = this.bounding.getSync(`${this.variant}.x`);
      const y = this.bounding.getSync(`${this.variant}.y`);

      // Check if stored positions are valid (within bounds)
      const xValid = x !== null && x !== undefined && x >= minX && x <= maxX;
      const yValid = y !== null && y !== undefined && y >= minY && y <= maxY;

      // If either position is invalid, reset both to center for consistent UX
      if (xValid && yValid) {
        frame.x = `${x}px`;
        frame.y = `${y}px`;
      } else {
        // Reset to centered position
        frame.x = `${centeredX}px`;
        frame.y = `${centeredY}px`;
        // Clear any invalid stored positions
        if (!xValid && x !== null && x !== undefined) {
          this.bounding.set(null, `${this.variant}.x`);
        }
        if (!yValid && y !== null && y !== undefined) {
          this.bounding.set(null, `${this.variant}.y`);
        }
      }
    }

    // Set styles
    if (frame.x) {
      this.renderer.setStyle(element, 'left', frame.x);
    }
    if (frame.y) {
      this.renderer.setStyle(element, 'top', frame.y);
    }
    if (frame.width) {
      this.renderer.setStyle(element, 'width', frame.width);
    }
    if (frame.height) {
      this.renderer.setStyle(element, 'height', frame.height);
    }
    if (this.minimized) {
      this.renderer.removeStyle(element, 'transform');
    }

    // Set last size to child modal
    if (!this.minimized) {
      const rect = this.elementRef.nativeElement.getBoundingClientRect();
      this.componentRef.instance.maximizedSize = new ModalFrame(rect.width, rect.height, rect.x, rect.y);
    }
  }

  /**
   * Delegates close to the child modal so it can run its own unsaved-changes
   * check against its locally provided `FormChangesModalsService`. The child
   * emits `closed` once the user confirms; this wrapper is subscribed to that
   * output in `ngOnInit` and forwards it as `onClose`.
   *
   * Falls back to `onClose.emit()` directly if the child ref is unavailable
   * (e.g. if `close()` is invoked before the dynamic component is created).
   */
  public close(): void {
    const instance = this.componentRef?.instance;
    if (instance) {
      instance.close();
    } else {
      this.onClose.emit();
    }
  }

  expand() {
    // this.popupService.compose(this.message, this.maximizedSize.popupSize());
    this.close();
  }
}
